import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Button, Checkbox, Divider, FormControlLabel, Grid, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import useClientContext from "../../../../../../context/useClientContext";
import SelectCompaniesDialog from "../../../../reporting/report-lists/reports/copyto/SelectCompaniesDialog";
import { useCopyToEligibleCompanies } from "../../../../reporting/report-lists/reports/hooks/useCopyToEligibleCompanies";
import type { DashboardValidationType } from "../../hooks/useDashboardValidation";
import CompaniesList from "./CompaniesList";
import ValidationDetailsContainer from "./ValidationDetailsContainer";
interface Props {
  validation: DashboardValidationType;
  copyConditionValues: boolean;
  onCopyConditionValues: (value: boolean) => void;
}
export default function CopyDashboardContent({ validation, copyConditionValues, onCopyConditionValues }: Props) {
  const { clientCode } = useClientContext();
  const eligibleCompanies = useCopyToEligibleCompanies(["ManageReports", "ManageDashboards"]);

  const [showSelectCompany, setShowSelectCompany] = useState(false);

  const selectedCompanyCodes = useMemo(() => validation?.selectedCompanies.map((c) => c.code), [validation]);

  const companyList = useMemo(() => {
    return eligibleCompanies
      .filter((c) => c.clientCode !== clientCode)
      .map((c): ListItemData => ({
        value: c.clientCode,
        label: c.title,
        iconSrc: c.branding.logoMarkUrl,
      }));
  }, [clientCode, eligibleCompanies]);

  return (
    <Grid container sx={{ width: "100%" }}>
      <Stack spacing={2} useFlexGap sx={{ flex: 1, px: 2.5, py: 1.5, width: "100%" }}>
        <FormControlLabel
          value={copyConditionValues}
          checked={copyConditionValues}
          control={<Checkbox />}
          label="Copy the condition values. If the values do not match or are missing in the target company, they will be omitted."
          onChange={(_, value) => onCopyConditionValues(value)}
        />
        <Typography color={"secondary"}>
          Please provide a list of the companies you wish to copy this dashboard to.
        </Typography>
        <Grid container sx={{ alignItems: "center", gap: 1, width: "100%" }}>
          <Typography variant="subtitle1">Companies</Typography>
          <BadgeDetached color="secondary" badgeContent={validation?.selectedCompanies.length ?? 0} />
          <Button variant="text" startIcon={<AddRoundedIcon />} onClick={() => setShowSelectCompany(true)}>
            Select Companies
          </Button>
        </Grid>
        <CompaniesList validation={validation} availableCompanies={companyList} />
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Grid container sx={{ flex: 0.6, width: "100%" }}>
        <ValidationDetailsContainer companyTile={validation.activeCompanyTile} />
      </Grid>
      <SelectCompaniesDialog
        open={showSelectCompany}
        availableCompanies={companyList}
        selectedCompanies={selectedCompanyCodes}
        handleClose={(companySelection) => {
          validation.updateCompanySelection(companySelection);
          setShowSelectCompany(false);
        }}
      />
    </Grid>
  );
}
